import { BorderStyleProperty, Flex } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';

import { useActiveReferenceBarID } from '../../controller/hooks/useActiveReferenceBarID.ts';
import { formatReferenceViewID } from '../../modules/referenceView/index.ts';
import {
  selectIncludeCostApproach,
  selectOrder,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectReferenceBarUI } from '../../redux/features/selectors/referenceBarUISelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { borderColor, borderStyle, borderWidth } from '../../styles/border.ts';
import { GuidedTourTooltip } from '../GuidedTour/GuidedTourTooltip.tsx';
import { AlertsItem } from './AlertsItem.tsx';
import { Tkv2ChartItem } from './Tkv2ChartsItem.tsx';
import { Tkv2ClientInformationItem } from './Tkv2ClientInformationItem.tsx';
import { Tkv2HelpDrawerItem } from './Tkv2HelpDrawerItem.tsx';
import { Tkv2MapItem } from './Tkv2MapItem.tsx';
import { Tkv2PdfReportItem } from './Tkv2PdfReportItem.tsx';
import { Tkv2ReconciliationItem } from './Tkv2ReconciliationItem.tsx';
import { Tkv2ReferenceBarProperty } from './Tkv2ReferenceBarProperty.tsx';
import { Tkv2ReferenceBarSection } from './Tkv2ReferenceBarSection.tsx';

import './Tkv2ReferenceBar.scss';

export const Tkv2ReferenceBar = () => {
  const { properties, mobileReferenceMenuOpen } = useAppSelector(selectReferenceBarUI);
  const order = useAppSelector((state) => selectOrder(state));
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const activeID = useActiveReferenceBarID();
  const [propertyExpansionDict, setPropertyExpansionDict] = useState<Record<string, boolean>>({
    subject: true,
  });

  useEffect(() => {
    const updatedExpansionDict: Record<string, boolean> = {
      subject: propertyExpansionDict.subject ?? true,
    };

    properties.forEach((prop) => {
      if (prop.id) {
        updatedExpansionDict[prop.id] = prop.id === activeID;
      }
    });

    setPropertyExpansionDict(updatedExpansionDict);
  }, [properties, activeID]);

  const handleExpansionClick = (propertyId: string) => {
    setPropertyExpansionDict({
      ...propertyExpansionDict,
      [propertyId]: !propertyExpansionDict[propertyId],
    });
  };

  return (
    <div
      className={`reference-bar-container${mobileReferenceMenuOpen ? ' reference-view-open' : ''}`}
    >
      <Flex
        flexDirection="column"
        height="100%"
        width="100%"
        overflowY="auto"
        overflowX="hidden"
        alignItems="flex-start"
        paddingTop={2}
        borderLeftWidth={borderWidth}
        borderColor={borderColor}
        borderStyle={borderStyle as BorderStyleProperty}
      >
        <AlertsItem />
        <Tkv2HelpDrawerItem />
        {order.isInspectifyOrder && <Tkv2ClientInformationItem />}
        <Tkv2MapItem />
        {!order.isInspectifyOrder && (
          <GuidedTourTooltip
            text={[
              'To complete the analysis, the site values, quality levels and effective ages of all properties must make sense in relationship to each other.',
              'In general, site value increases with site size. Click here to view site charts that will help you work through the analysis.',
            ]}
            tourID="analysisTour"
            tourIndex={7}
            placement="left"
          >
            <Tkv2ChartItem />
          </GuidedTourTooltip>
        )}
        {order.isInspectifyOrder && <Tkv2ReconciliationItem />}
        {order.isInspectifyOrder && <Tkv2PdfReportItem />}
        <Tkv2ReferenceBarSection
          property={
            <Tkv2ReferenceBarProperty
              property={{
                id: formatReferenceViewID({
                  type: 'subject',
                }),
                mapPinLabel: '',
              }}
              expanded={propertyExpansionDict.subject ?? true}
              onExpansionClick={handleExpansionClick}
            />
          }
        />
        {properties.map((prop) =>
          prop.id ? (
            <Tkv2ReferenceBarSection
              key={`reference-bar-${prop.id}`}
              property={
                <Tkv2ReferenceBarProperty
                  property={prop}
                  expanded={propertyExpansionDict[prop.id] ?? true}
                  onExpansionClick={handleExpansionClick}
                />
              }
            />
          ) : null,
        )}
      </Flex>
    </div>
  );
};
